import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Bot, 
  Users, 
  Megaphone,
  ArrowRight,
  Shield,
  Zap,
  Globe,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // If user is already logged in, redirect to dashboard
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const features = [
    {
      icon: <MessageSquare className="h-6 w-6 text-blue-500" />,
      title: "WhatsApp Business API",
      description: "Conecta tu cuenta de WhatsApp Business y comienza a enviar mensajes de forma automatizada."
    },
    {
      icon: <Bot className="h-6 w-6 text-green-500" />,
      title: "Chatbots Inteligentes",
      description: "Crea flujos de conversación personalizados con nuestro editor visual intuitivo."
    },
    {
      icon: <Users className="h-6 w-6 text-purple-500" />,
      title: "Gestión de Contactos",
      description: "Organiza tus contactos con etiquetas y segmenta tu audiencia de manera eficiente."
    },
    {
      icon: <Megaphone className="h-6 w-6 text-orange-500" />,
      title: "Campañas Masivas",
      description: "Envía mensajes a múltiples contactos y mide el rendimiento de tus campañas."
    }
  ];

  const benefits = [
    {
      icon: <Shield className="h-5 w-5 text-blue-600" />,
      title: "Seguro y Confiable",
      description: "Conexión segura con la API oficial de WhatsApp Business."
    },
    {
      icon: <Zap className="h-5 w-5 text-yellow-500" />,
      title: "Rápido y Eficiente",
      description: "Automatiza tus conversaciones y ahorra tiempo valioso."
    },
    {
      icon: <Globe className="h-5 w-5 text-green-600" />,
      title: "Escalable",
      description: "Crece sin límites con nuestra infraestructura robusta."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 lg:mt-16 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Automatiza tu</span>
                  <span className="block text-blue-600">WhatsApp Business</span>
                </h1>
                <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                  Potencia tu negocio con chatbots inteligentes, campañas automatizadas y análisis detallado de conversaciones.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <div className="rounded-md shadow">
                    <button
                      onClick={() => navigate('/login')}
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10"
                    >
                      Iniciar Sesión
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </button>
                  </div>
                  <div className="mt-3 sm:mt-0 sm:ml-3">
                    <button
                      onClick={() => navigate('/register')}
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 md:py-4 md:text-lg md:px-10"
                    >
                      Registrarse
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <img
            className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
            src="https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80"
            alt="WhatsApp Business Dashboard"
          />
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Características</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Todo lo que necesitas para tu negocio
            </p>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => (
                <div key={index} className="relative bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <div className="absolute h-12 w-12 flex items-center justify-center rounded-md bg-gray-100">
                      {feature.icon}
                    </div>
                    <p className="ml-16 text-lg leading-6 font-medium text-gray-900">{feature.title}</p>
                  </div>
                  <div className="mt-4 ml-16 text-base text-gray-500">
                    {feature.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gray-50 pt-12 sm:pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Beneficios de nuestra plataforma
            </h2>
            <p className="mt-3 text-xl text-gray-500 sm:mt-4">
              Diseñada para hacer crecer tu negocio de manera inteligente
            </p>
          </div>
        </div>
        <div className="mt-10 pb-12 bg-white sm:pb-16">
          <div className="relative">
            <div className="absolute inset-0 h-1/2 bg-gray-50" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto">
                <div className="rounded-lg bg-white shadow-lg sm:grid sm:grid-cols-3">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className={`p-6 ${
                        index === 0
                          ? 'rounded-t-lg sm:rounded-tr-none sm:rounded-l-lg'
                          : index === benefits.length - 1
                          ? 'rounded-b-lg sm:rounded-bl-none sm:rounded-r-lg'
                          : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {benefit.icon}
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium text-gray-900">
                            {benefit.title}
                          </h3>
                          <p className="mt-2 text-base text-gray-500">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-blue-600">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">¿Listo para comenzar?</span>
            <span className="block text-blue-200">Configura tu cuenta en minutos</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-blue-100">
            Comienza a automatizar tus conversaciones de WhatsApp hoy mismo.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 sm:w-auto"
          >
            <CheckCircle className="h-5 w-5 mr-2" />
            Crear Cuenta Gratis
          </button>
        </div>
      </div>
    </div>
  );
}